import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

const StyledFooter = styled('footer')(({ theme }) => ({
  width: '100%',
  marginTop: 'auto',
  backgroundColor: 'rgba(26, 26, 31, 0.95)',
  backdropFilter: 'blur(20px)',
  borderTop: `2px solid rgba(0, 255, 255, 0.2)`,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #00ffff, #ff00ff, #00ff00, #00ffff)',
    backgroundSize: '200% 100%',
    animation: 'gradient-shift 3s linear infinite',
  },
  
  '@keyframes gradient-shift': {
    '0%': { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '200% 50%' },
  },
}));

const FooterContent = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'center',
  
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'flex-start',
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontFamily: '"Orbitron", "Noto Sans JP", sans-serif',
  fontSize: '0.9rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  position: 'relative',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: 0,
  border: '1px solid transparent',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
    transition: 'width 0.3s ease',
  },
  
  '&:hover': {
    color: theme.palette.neon.cyan,
    transform: 'translateY(-2px)',
    textShadow: `0 0 10px ${theme.palette.neon.cyan}`,
    borderColor: 'rgba(0, 255, 255, 0.3)',
    backgroundColor: 'rgba(0, 255, 255, 0.05)',
    
    '&::before': {
      width: '100%',
    },
    
    '& .footer-icon': {
      transform: 'rotate(360deg)',
      filter: `drop-shadow(0 0 5px ${theme.palette.neon.cyan})`,
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
    padding: theme.spacing(0.5),
  },
}));

const Copyright = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontSize: '0.85rem',
  fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
  fontWeight: 500,
  letterSpacing: '0.1em',
  textAlign: 'center',
  position: 'relative',
  
  '& .copyright-symbol': {
    color: theme.palette.neon.magenta,
    textShadow: `0 0 10px ${theme.palette.neon.magenta}`,
    animation: 'pulse 2s ease-in-out infinite',
  },
  
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.8 },
    '50%': { opacity: 1 },
  },
  
  [theme.breakpoints.up('sm')]: {
    textAlign: 'right',
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
  },
}));

const Footer = () => {
  return (
    <StyledFooter>
      <FooterContent maxWidth="xl">
        <FooterLinks>
          <FooterLink to="/about">
            <InfoOutlinedIcon 
              className="footer-icon" 
              sx={{ 
                fontSize: '1rem',
                transition: 'all 0.3s ease',
              }} 
            />
            <span>About</span>
          </FooterLink>
          <FooterLink to="/privacy-policy">
            <PolicyOutlinedIcon 
              className="footer-icon" 
              sx={{ 
                fontSize: '1rem',
                transition: 'all 0.3s ease',
              }} 
            />
            <span>Privacy</span>
          </FooterLink>
          <FooterLink to="/contact">
            <EmailOutlinedIcon 
              className="footer-icon" 
              sx={{ 
                fontSize: '1rem',
                transition: 'all 0.3s ease',
              }} 
            />
            <span>Contact</span>
          </FooterLink>
        </FooterLinks>
        <Copyright variant="body2">
          <span className="copyright-symbol">©</span> E-FOMATE
        </Copyright>
      </FooterContent>
    </StyledFooter>
  );
};

export default Footer;
